import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'

import Button from '@renderer/components/Button'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import { useCameraPresetStore, type StoredPreset } from '@renderer/stores/cameraPresetStore'
import {
  applyPresetPlan,
  getRollback,
  isCrossModelApplyEnabled,
  planPresetApply,
  selectAppSettingsToApply,
  undoLastApply,
  type ApplyOptions,
  type ApplyResult,
  type CameraKey,
  type PlanResult
} from '@renderer/utils/cameras/cameraPresets'
import {
  detectMatchTier,
  readCameraModel,
  type ConfigNode,
  type MatchTier,
  type PresetGroup,
  type SkipReason
} from '@renderer/utils/cameras/presetCompatibility'
import { detectCameraBrandFromConfig } from '@renderer/utils/cameras/captureRiskAssessment'

/**
 * Camera presets, inside the Camera Settings modal.
 *
 * The operator never names anything: rows are labelled by the project they were
 * captured on ("Jogja — Photobox Tertutup · Canon EOS R100"), which is already
 * how they think about their booths. The panel's job is to surface the row that
 * matches the connected camera, show honestly what applying it would change,
 * and then hand the write over to the engine.
 *
 * The one piece of machinery that lives HERE rather than in the engine is
 * preview suspension: the live preview is React-owned, and `parkCamera` refuses
 * outright while a Preview is mounted. Unmounting it is therefore the
 * load-bearing safety step, and only the modal can do it.
 */

interface CameraPresetsPanelProps {
  cameraKey: CameraKey
  config: ConfigNode | null
  isConnected: boolean
  /**
   * Increments on every manual widget edit in the tree below. Compared against
   * the value recorded at apply time to detect "modified since applied" — the
   * camera is the source of truth, and an operator who hand-edits after an
   * apply must not keep being told the preset is still in effect.
   */
  configRevision: number
  /** Hides the modal's live Preview so the pull loop stops during an apply. */
  onSuspendPreviewChange: (suspended: boolean) => void
  /** Called after we rewrite the camera, so the modal re-reads the widget tree. */
  onConfigChangedExternally: () => void
}

const ALL_GROUPS: PresetGroup[] = ['exposure', 'image', 'behaviour']

const TIER_ORDER: Record<MatchTier, number> = { exact: 0, brand: 1, cross: 2 }

const CameraPresetsPanel: React.FC<CameraPresetsPanelProps> = ({
  cameraKey,
  config,
  isConnected,
  configRevision,
  onSuspendPreviewChange,
  onConfigChangedExternally
}) => {
  const { t } = useTranslation()
  const presets = useCameraPresetStore((s) => s.presets)
  const projectNames = useCameraPresetStore((s) => s.projectNames)
  const fetchPresets = useCameraPresetStore((s) => s.fetchPresets)
  const loaded = useCameraPresetStore((s) => s.loaded)
  const setPinned = useCameraPresetStore((s) => s.setPinned)

  const setCameraSettings = useSettingsStore((s) => s.setCameraSettings)
  const setSecondaryCameraSettings = useSettingsStore((s) => s.setSecondaryCameraSettings)
  const recordApply = useBoothEventStore((s) => s.recordCameraPresetApply)

  const [selectedId, setSelectedId] = useState<string>('')
  const [enabledGroups, setEnabledGroups] = useState<PresetGroup[]>(ALL_GROUPS)
  const [includeGeometry, setIncludeGeometry] = useState(false)
  const [plan, setPlan] = useState<PlanResult | null>(null)
  const [planning, setPlanning] = useState(false)
  const [applying, setApplying] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number; label: string } | null>(
    null
  )
  const [result, setResult] = useState<ApplyResult | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const cancelRef = useRef(false)

  /**
   * Which preset this camera is currently running, and the widget revision it
   * was at when we applied it. Any hand-edit past that revision means the
   * camera no longer matches the preset.
   */
  const [applied, setApplied] = useState<{ id: string; label: string; revision: number } | null>(
    null
  )
  const [showGroupPicker, setShowGroupPicker] = useState(false)
  const [pinBusy, setPinBusy] = useState(false)
  const [renameDraft, setRenameDraft] = useState('')
  const [renaming, setRenaming] = useState(false)

  const isModified = applied !== null && configRevision > applied.revision
  const canUndo = getRollback(cameraKey) !== null && !applying

  useEffect(() => {
    if (!loaded) void fetchPresets()
  }, [loaded, fetchPresets])

  const targetModel = useMemo(() => readCameraModel(config), [config])
  const targetBrand = useMemo(() => detectCameraBrandFromConfig(config), [config])

  /** Every preset, tagged with how well it matches the camera in front of us. */
  const ranked = useMemo(() => {
    const crossAllowed = isCrossModelApplyEnabled()
    return presets
      .map((preset) => ({
        preset,
        tier: detectMatchTier(preset.cameraModel, preset.cameraBrand, targetModel, targetBrand)
      }))
      .filter((row) => crossAllowed || row.tier === 'exact')
      .sort((a, b) => {
        const byTier = TIER_ORDER[a.tier] - TIER_ORDER[b.tier]
        if (byTier !== 0) return byTier
        return String(b.preset.capturedAt || '').localeCompare(String(a.preset.capturedAt || ''))
      })
  }, [presets, targetModel, targetBrand])

  const suggestion = ranked[0]
  const selected = useMemo(
    () => ranked.find((r) => r.preset._id === selectedId) || suggestion,
    [ranked, selectedId, suggestion]
  )

  const labelFor = useCallback(
    (preset: StoredPreset): string => {
      const project = projectNames[preset.projectId] || preset.projectId || t('cameraPresets.unknownProject', 'Unknown booth')
      const name = preset.isPinned && preset.name ? preset.name : project
      return preset.cameraModel ? `${name} · ${preset.cameraModel}` : name
    },
    [projectNames, t]
  )

  /** Group names, said the way an operator would say them. */
  const groupTitle = useCallback(
    (group: PresetGroup): string =>
      group === 'exposure'
        ? t('cameraPresets.groups.exposure.title', 'How bright the photo is')
        : group === 'image'
          ? t('cameraPresets.groups.image.title', 'Colour and quality')
          : t('cameraPresets.groups.behaviour.title', 'How the camera shoots'),
    [t]
  )

  /** The actual settings behind each group — the part that was missing. */
  const groupExamples = useCallback(
    (group: PresetGroup): string =>
      group === 'exposure'
        ? t('cameraPresets.groups.exposure.examples', 'ISO, shutter speed, aperture, metering')
        : group === 'image'
          ? t('cameraPresets.groups.image.examples', 'White balance, picture style, file size')
          : t('cameraPresets.groups.behaviour.examples', 'Focus mode, drive mode, bracketing'),
    [t]
  )

  const summaryOfSelection = useMemo(() => {
    const parts: string[] = []
    for (const group of ALL_GROUPS) if (enabledGroups.includes(group)) parts.push(groupTitle(group))
    if (includeGeometry) {
      parts.push(t('cameraPresets.groups.geometry.title', 'How the picture is framed'))
    }
    if (parts.length === 0) return t('cameraPresets.groups.none', 'Nothing selected to copy.')
    return t('cameraPresets.groups.summary', 'Copies: {{list}}.', {
      list: parts.join(', ').toLowerCase()
    })
  }, [enabledGroups, groupTitle, includeGeometry, t])

  const tierLabel = useCallback(
    (tier: MatchTier): string =>
      tier === 'exact'
        ? t('cameraPresets.tier.exact', 'Exact match')
        : tier === 'brand'
          ? t('cameraPresets.tier.brand', 'Same brand')
          : t('cameraPresets.tier.cross', 'Other camera'),
    [t]
  )

  const skipLabel = useCallback(
    (reason: SkipReason): string => {
      switch (reason) {
        case 'missing-on-target':
          return t('cameraPresets.skip.missing', 'this camera has no such setting')
        case 'readonly':
          return t('cameraPresets.skip.readonly', 'set on the camera body, not over USB')
        case 'no-matching-choice':
          return t('cameraPresets.skip.noChoice', 'no comparable value on this camera')
        case 'mode-locked':
          return t(
            'cameraPresets.skip.modeLocked',
            'turn the mode dial to M to apply this'
          )
        case 'group-disabled':
          return t('cameraPresets.skip.groupOff', 'group turned off above')
        case 'denied':
          return t('cameraPresets.skip.denied', 'not safe to change automatically')
        case 'cross-model-disabled':
          return t(
            'cameraPresets.skip.crossDisabled',
            'copying between different cameras is turned off'
          )
        default:
          return reason
      }
    },
    [t]
  )

  /**
   * `override` exists for Revert: the operator may have changed the dropdown
   * since applying, and reverting must re-plan the preset the camera is
   * actually running — not whatever happens to be selected now.
   */
  const buildPlan = useCallback(
    async (override?: StoredPreset): Promise<void> => {
      const source = override ?? selected?.preset
      if (!source) return
      setPlanning(true)
      setResult(null)
      try {
        const next = await planPresetApply(cameraKey, {
          entries: source.entries || [],
          presetModel: source.cameraModel,
          presetBrand: source.cameraBrand,
          enabledGroups
        })
        if (!next) {
          cogoToast.error(
            t(
              'cameraPresets.planFailed',
              'Could not read the camera. Check the cable and try again.'
            )
          )
          return
        }
        setPlan(next)
        setShowDetail(false)
      } finally {
        setPlanning(false)
      }
    },
    [cameraKey, enabledGroups, selected, t]
  )

  /**
   * Shared by apply, revert and undo — all three drive the same engine and all
   * three must suspend the preview the same way. Unmounting the Preview stops
   * the frame pull loop; without it park() refuses and every write fights a
   * liveview read on the one PTP channel.
   */
  const applyOptions = useCallback(
    (): ApplyOptions => ({
      suspendPreview: () => onSuspendPreviewChange(true),
      resumePreview: () => onSuspendPreviewChange(false),
      shouldCancel: () => cancelRef.current,
      onProgress: (done, total, label) => setProgress({ done, total, label })
    }),
    [onSuspendPreviewChange]
  )

  const runApply = useCallback(async (): Promise<void> => {
    if (!plan || !selected) return
    cancelRef.current = false
    setApplying(true)
    setProgress({ done: 0, total: plan.plan.writes.length, label: '' })

    const applyResult = await applyPresetPlan(cameraKey, plan.plan, applyOptions())

    // App-side settings are applied only once the camera part is done, and only
    // if it did not leave the camera unwell — they are cosmetic next to a body
    // that has stopped answering.
    if (applyResult.status === 'completed' || applyResult.status === 'partial') {
      const appSettings = selectAppSettingsToApply(selected.preset.appSettings, includeGeometry)
      if (Object.keys(appSettings).length > 0) {
        if (cameraKey === 'primary') setCameraSettings(appSettings)
        else setSecondaryCameraSettings(appSettings)
      }
    }

    recordApply({
      cameraKey,
      status: applyResult.status,
      tier: plan.plan.tier,
      applied: applyResult.applied,
      adjusted: applyResult.adjusted,
      skipped: applyResult.skipped,
      failed: applyResult.failures.length,
      durationMs: applyResult.durationMs,
      cameraHealthy: applyResult.cameraHealthy,
      sourceProjectId: selected.preset.projectId,
      cameraModel: selected.preset.cameraModel,
      reason: applyResult.reason
    })

    setResult(applyResult)
    setProgress(null)
    setApplying(false)
    setPlan(null)

    // Remember what this camera is now running, pinned to the widget revision at
    // this moment. A later hand-edit pushes the revision past it and flips the
    // banner to "Modified".
    if (applyResult.status === 'completed' || applyResult.status === 'partial') {
      setApplied({
        id: selected.preset._id,
        label: labelFor(selected.preset),
        revision: configRevision
      })
      // The widget tree below is now stale — the camera changed underneath it.
      onConfigChangedExternally()
    }

    if (!applyResult.cameraHealthy) {
      cogoToast.error(
        t(
          'cameraPresets.unhealthy',
          'The camera stopped responding. Unplug and reconnect it before the next session.'
        ),
        { hideAfter: 12 }
      )
    }
  }, [
    applyOptions,
    cameraKey,
    configRevision,
    includeGeometry,
    labelFor,
    onConfigChangedExternally,
    plan,
    recordApply,
    selected,
    setCameraSettings,
    setSecondaryCameraSettings,
    t
  ])

  /**
   * Undo the last apply — put back the values that were on the camera before it.
   *
   * Never automatic. A failed apply means the camera is already unhappy, and
   * firing another N writes at it is how a partial change becomes a wedged body,
   * so this is the operator's decision and the engine refuses when the camera
   * has stopped answering.
   */
  const runUndo = useCallback(async (): Promise<void> => {
    cancelRef.current = false
    setApplying(true)
    setProgress({ done: 0, total: 0, label: '' })

    const undoResult = await undoLastApply(cameraKey, applyOptions())

    recordApply({
      kind: 'preset',
      cameraKey,
      status: undoResult.status,
      tier: 'undo',
      applied: undoResult.applied,
      adjusted: undoResult.adjusted,
      skipped: undoResult.skipped,
      failed: undoResult.failures.length,
      durationMs: undoResult.durationMs,
      cameraHealthy: undoResult.cameraHealthy,
      reason: undoResult.reason
    })

    setResult(undoResult)
    setProgress(null)
    setApplying(false)
    if (undoResult.status === 'completed') setApplied(null)
    onConfigChangedExternally()
  }, [applyOptions, cameraKey, onConfigChangedExternally, recordApply])

  /**
   * Re-apply the preset the camera was put on, discarding hand-edits made since.
   * Goes through the same plan → confirm path as a first apply rather than
   * writing blind: the operator still sees what will change.
   */
  const runRevert = useCallback(async (): Promise<void> => {
    if (!applied) return
    const source = presets.find((p) => p._id === applied.id)
    if (!source) {
      cogoToast.error(
        t('cameraPresets.revertGone', 'That saved configuration is no longer available.')
      )
      return
    }
    // Point the picker at it too, so Apply below acts on what Revert planned.
    setSelectedId(applied.id)
    setResult(null)
    await buildPlan(source)
  }, [applied, buildPlan, presets, t])

  const togglePin = useCallback(async (): Promise<void> => {
    if (!selected) return
    setPinBusy(true)
    const next = !selected.preset.isPinned
    const failure = await setPinned(
      selected.preset._id,
      next,
      next ? selected.preset.name || projectNames[selected.preset.projectId] || null : null
    )
    setPinBusy(false)
    if (failure) {
      cogoToast.error(
        t('cameraPresets.keepFailed', 'Could not save that ({{detail}}). Try again.', {
          detail: failure
        })
      )
      return
    }
    setRenaming(false)
  }, [projectNames, selected, setPinned, t])

  const commitRename = useCallback(async (): Promise<void> => {
    if (!selected) return
    const name = renameDraft.trim()
    setPinBusy(true)
    const failure = await setPinned(selected.preset._id, true, name.length > 0 ? name : null)
    setPinBusy(false)
    if (failure) {
      cogoToast.error(
        t('cameraPresets.renameFailed', 'Could not rename ({{detail}}). Try again.', {
          detail: failure
        })
      )
      return
    }
    setRenaming(false)
  }, [renameDraft, selected, setPinned, t])

  const toggleGroup = (group: PresetGroup): void => {
    setPlan(null)
    setEnabledGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    )
  }

  if (!isConnected) return null

  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-blue-900">
          {t('cameraPresets.title', 'Copy settings from another booth')}
        </h3>
        {presets.length > 0 && (
          <span className="text-xs text-blue-700">
            {t('cameraPresets.count', '{{count}} saved', { count: presets.length })}
          </span>
        )}
      </div>

      {ranked.length === 0 ? (
        <p className="mt-2 text-xs text-blue-800">
          {t(
            'cameraPresets.empty',
            'No saved settings yet. This booth saves its own when you close this window, and you can copy it onto another booth from there.'
          )}
        </p>
      ) : (
        <>
          {/* The match, surfaced but never auto-applied. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select
              value={selected?.preset._id || ''}
              onChange={(e) => {
                setSelectedId(e.target.value)
                setPlan(null)
                setResult(null)
              }}
              disabled={applying}
              className="min-w-0 flex-1 rounded-md border border-blue-300 bg-white px-2 py-1.5 text-xs"
              aria-label={t('cameraPresets.choose', 'Choose a booth to copy from')}
            >
              {/* Grouped by how well each row matches the camera in front of
                  the operator, so "will this work on my body?" is answered by
                  the list's shape rather than by reading every label. */}
              {(['exact', 'brand', 'cross'] as MatchTier[]).map((tier) => {
                const rows = ranked.filter((row) => row.tier === tier)
                if (rows.length === 0) return null
                return (
                  <optgroup key={tier} label={tierLabel(tier)}>
                    {rows.map(({ preset }) => (
                      <option key={preset._id} value={preset._id}>
                        {preset.isPinned ? '📌 ' : ''}
                        {labelFor(preset)}
                      </option>
                    ))}
                  </optgroup>
                )
              })}
            </select>
            <Button
              text={
                planning
                  ? t('cameraPresets.checking', 'Checking the camera…')
                  : t('cameraPresets.use', 'Use these settings')
              }
              onClick={() => void buildPlan()}
              disabled={planning || applying || !selected}
              className="!px-3 !py-1.5 !text-xs"
            />
          </div>

          {/* Pin — the only way to stop auto-snapshot overwriting a
              configuration the operator wants to keep as a reference. Without
              this, tuning the booth silently replaces the very row someone else
              copies from. A pinned row may also carry its own name; an unpinned
              one is always labelled by its project. */}
          {selected && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => void togglePin()}
                disabled={pinBusy || applying}
                className={`rounded-full border px-2.5 py-1 ${
                  selected.preset.isPinned
                    ? 'border-blue-400 bg-blue-100 text-blue-900'
                    : 'border-gray-300 bg-white text-gray-700'
                }`}
              >
                {selected.preset.isPinned
                  ? t('cameraPresets.keptTitle', '📌 Kept as reference — auto-save leaves it alone')
                  : t('cameraPresets.keep', 'Keep as reference')}
              </button>

              {selected.preset.isPinned && !renaming && (
                <button
                  type="button"
                  onClick={() => {
                    setRenameDraft(selected.preset.name || labelFor(selected.preset))
                    setRenaming(true)
                  }}
                  disabled={pinBusy || applying}
                  className="text-blue-700 underline"
                >
                  {t('cameraPresets.rename', 'Rename')}
                </button>
              )}

              {selected.preset.isPinned && renaming && (
                <>
                  <input
                    type="text"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    maxLength={80}
                    autoFocus
                    className="min-w-0 flex-1 rounded-md border border-blue-300 px-2 py-1"
                    aria-label={t('cameraPresets.renameLabel', 'Preset name')}
                  />
                  <button
                    type="button"
                    onClick={() => void commitRename()}
                    disabled={pinBusy}
                    className="text-blue-700 underline"
                  >
                    {t('common.save', 'Save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRenaming(false)}
                    className="text-gray-500 underline"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Applied-then-hand-edited. The camera is the source of truth, so
              once the operator changes a widget the preset is no longer what is
              running — say so instead of implying otherwise. */}
          {isModified && applied && !applying && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
              <span>
                {t('cameraPresets.modifiedFrom', 'Modified from {{label}}', {
                  label: applied.label
                })}
              </span>
              <button
                type="button"
                onClick={() => void runRevert()}
                disabled={planning}
                className="underline"
              >
                {t('cameraPresets.revert', 'Revert')}
              </button>
            </div>
          )}

          {selected && selected.tier !== 'exact' && (
            <p className="mt-2 text-xs text-amber-800">
              {t(
                'cameraPresets.approxWarning',
                'This was saved on a different camera ({{model}}). Values that do not exist here will be skipped, and close ones will be approximated.',
                { model: selected.preset.cameraModel || t('cameraPresets.unknownModel', 'unknown') }
              )}
            </p>
          )}

          {/* What gets copied.
              This used to be four bare checkboxes — "Exposure / Image / Capture
              behaviour / Mirror & rotation" — and nobody could tell what they
              mapped to, which makes them a decision an operator cannot make
              rather than a control. The default now states itself in one line,
              and the choice is available for the rare case that needs it, with
              each group named by the settings it actually contains. */}
          <div className="mt-3 text-xs text-blue-900">
            <div className="flex flex-wrap items-center gap-2">
              <span>{summaryOfSelection}</span>
              <button
                type="button"
                onClick={() => setShowGroupPicker((v) => !v)}
                disabled={applying}
                className="underline"
              >
                {showGroupPicker
                  ? t('cameraPresets.groups.hide', 'Done')
                  : t('cameraPresets.groups.choose', 'Choose what to copy')}
              </button>
            </div>

            {showGroupPicker && (
              <div className="mt-2 space-y-1.5 rounded-md border border-blue-200 bg-white p-2.5">
                {ALL_GROUPS.map((group) => (
                  <label key={group} className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={enabledGroups.includes(group)}
                      onChange={() => toggleGroup(group)}
                      disabled={applying}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium text-gray-800">{groupTitle(group)}</span>
                      <span className="block text-[11px] text-gray-500">
                        {groupExamples(group)}
                      </span>
                    </span>
                  </label>
                ))}
                <label className="flex items-start gap-2 border-t border-gray-100 pt-1.5">
                  <input
                    type="checkbox"
                    checked={includeGeometry}
                    onChange={() => setIncludeGeometry((v) => !v)}
                    disabled={applying}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-medium text-gray-800">
                      {t('cameraPresets.groups.geometry.title', 'How the picture is framed')}
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      {t(
                        'cameraPresets.groups.geometry.examples',
                        'Mirror, rotation, crop ratio — these depend on how this booth is physically built. Only copy them between identical builds.'
                      )}
                    </span>
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* The dry run. Nothing is written until this is confirmed. */}
          {plan && !applying && (
            <div className="mt-3 rounded-md border border-blue-200 bg-white p-3">
              <p className="text-xs font-medium text-gray-800">
                {t('cameraPresets.planSummary', '{{apply}} will change · {{adjust}} approximated · {{skip}} skipped', {
                  apply: plan.plan.writes.length,
                  adjust: plan.plan.writes.filter((w) => w.adjusted).length,
                  skip: plan.plan.skipped.length
                })}
              </p>
              {plan.plan.noOps.length > 0 && (
                <p className="mt-1 text-[11px] text-gray-500">
                  {t('cameraPresets.alreadyMatching', '{{count}} settings already match — they will not be touched.', {
                    count: plan.plan.noOps.length
                  })}
                </p>
              )}

              <button
                type="button"
                onClick={() => setShowDetail((v) => !v)}
                className="mt-2 text-[11px] text-blue-700 underline"
              >
                {showDetail
                  ? t('cameraPresets.hideDetail', 'Hide details')
                  : t('cameraPresets.showDetail', 'Show details')}
              </button>

              {showDetail && (
                <div className="mt-2 max-h-40 overflow-y-auto text-[11px]">
                  {plan.plan.writes.map((w) => (
                    <div key={`w-${w.targetName}`} className="flex justify-between gap-2 py-0.5">
                      <span className="text-gray-700">{w.entry.label || w.targetName}</span>
                      <span className="text-gray-900">
                        {String(w.value)}
                        {w.adjusted && (
                          <span className="text-amber-700">
                            {' '}
                            ({t('cameraPresets.wanted', 'wanted {{v}}', { v: String(w.requested) })})
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  {plan.plan.skipped.map((s) => (
                    <div
                      key={`s-${s.entry.name}-${s.reason}`}
                      className="flex justify-between gap-2 py-0.5 text-gray-400"
                    >
                      <span>{s.entry.label || s.entry.name}</span>
                      <span>{skipLabel(s.reason)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex gap-2">
                <Button
                  text={t('cameraPresets.confirm', 'Yes, change the camera')}
                  onClick={() => void runApply()}
                  disabled={plan.plan.writes.length === 0}
                  className="!px-3 !py-1.5 !text-xs"
                />
                <Button
                  text={t('common.cancel', 'Cancel')}
                  variant="secondary"
                  onClick={() => setPlan(null)}
                  className="!px-3 !py-1.5 !text-xs"
                />
              </div>
              <p className="mt-2 text-[11px] text-gray-500">
                {t(
                  'cameraPresets.applyNotice',
                  'The live preview pauses while this runs. Do not unplug the camera.'
                )}
              </p>
            </div>
          )}

          {/* In flight. Cancel takes effect between settings — a write already
              sent to the camera cannot be called back. */}
          {applying && progress && (
            <div className="mt-3 rounded-md border border-blue-200 bg-white p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-800">
                  {t('cameraPresets.applying', 'Applying {{done}} of {{total}}…', {
                    done: progress.done,
                    total: progress.total
                  })}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    cancelRef.current = true
                  }}
                  className="text-[11px] text-red-700 underline"
                >
                  {t('cameraPresets.stop', 'Stop')}
                </button>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{
                    width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%`
                  }}
                />
              </div>
              <p className="mt-1 truncate text-[11px] text-gray-500">{progress.label}</p>
            </div>
          )}

          {result && !applying && (
            <div
              className={`mt-3 rounded-md p-3 text-xs ${
                !result.cameraHealthy
                  ? 'border border-red-300 bg-red-50 text-red-900'
                  : result.status === 'completed'
                    ? 'border border-green-300 bg-green-50 text-green-900'
                    : 'border border-amber-300 bg-amber-50 text-amber-900'
              }`}
            >
              <p className="font-medium">
                {t('cameraPresets.resultSummary', '{{applied}} applied · {{skipped}} skipped', {
                  applied: result.applied,
                  skipped: result.skipped
                })}
                {result.failures.length > 0 &&
                  ` · ${t('cameraPresets.resultFailed', '{{count}} failed', {
                    count: result.failures.length
                  })}`}
              </p>
              {result.reason && <p className="mt-1">{result.reason}</p>}
              {!result.cameraHealthy && (
                <p className="mt-1 font-medium">
                  {t(
                    'cameraPresets.reconnect',
                    'Unplug and reconnect the camera before running a session.'
                  )}
                </p>
              )}
              {/* Undo is offered only while the camera is still answering:
                  putting the old values back means more writes, and a camera
                  that has stopped responding must not be given any. */}
              {canUndo && result.cameraHealthy && (
                <button
                  type="button"
                  onClick={() => void runUndo()}
                  className="mt-2 underline"
                >
                  {t('cameraPresets.undo', 'Undo — put the previous settings back')}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default CameraPresetsPanel
